// Sprint 7 (Phase 8.3) — deep-link routing for Telegram URLs.
//
// Telegram messages embed `https://ide.asoasis.com/#agent=<agentId>`. We
// use the URL fragment (not a query string) because:
//   - It survives without server-side route handling — React Router
//     SPA serves index.html for everything; the hash is parsed in JS.
//   - No browser-history pollution: we wipe the fragment after consuming
//     it (history.replaceState), so a refresh doesn't re-fire the
//     selection.
//
// Behavior:
//   1. On mount (after authed), parse window.location.hash for
//      `agent=<id>`. Also accept query string `?agent=<id>` for
//      resilience (links pasted from elsewhere).
//   2. Wait until the agents map has at least one entry (or 1.5 s,
//      whichever first) — we need to know whether the agent exists
//      before deciding select vs. toast.
//   3. If the id matches a known agent → selectAgent(id). Else → toast
//      "Agent not found" + load default view.
//   4. Either way, clear the hash from the URL so a refresh starts
//      clean.
//
// This hook is idempotent: it only acts once per page-load. Mounted from
// HomePage so it runs only after auth gate passes.

import { useEffect, useRef } from 'react';
import { useAppStore } from '../stores/app.store';
import { useToastStore } from '../stores/toast.store';

interface ParsedTarget {
  agentId: string;
  source: 'hash' | 'query';
}

export function parseDeepLink(url: string): ParsedTarget | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  // Hash: `#agent=abc` or `#/agent=abc` or even `#agent=abc&foo=1`.
  // We extract via URLSearchParams over the post-`#` substring (drop
  // any leading `/` to be lenient).
  const rawHash = parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash;
  const hashParams = new URLSearchParams(rawHash.startsWith('/') ? rawHash.slice(1) : rawHash);
  const fromHash = hashParams.get('agent');
  if (fromHash) {
    return { agentId: fromHash, source: 'hash' };
  }
  const fromQuery = parsed.searchParams.get('agent');
  if (fromQuery) {
    return { agentId: fromQuery, source: 'query' };
  }
  return null;
}

interface UseDeepLinkOptions {
  /** Test seam — defaults to window. */
  windowRef?: Window;
  /** Test seam — how long to wait for the agent map to populate. */
  resolveTimeoutMs?: number;
}

export function useDeepLink(opts: UseDeepLinkOptions = {}): void {
  const fired = useRef(false);
  const win = opts.windowRef ?? (typeof window !== 'undefined' ? window : undefined);
  const timeoutMs = opts.resolveTimeoutMs ?? 1500;

  useEffect(() => {
    if (fired.current) return;
    if (!win) return;
    const target = parseDeepLink(win.location.href);
    if (!target) return;
    fired.current = true;

    function clearFragment() {
      // Wipe the hash + agent= query so refresh doesn't re-trigger.
      const url = new URL(win!.location.href);
      url.hash = '';
      url.searchParams.delete('agent');
      win!.history.replaceState(null, '', url.toString());
    }

    function tryResolve(): boolean {
      const state = useAppStore.getState();
      // Are agents loaded? agents map starts empty; it's only populated
      // once loadAgents resolves. We wait until at least one agent is
      // present OR the explicit target id surfaces.
      const agents = state.agents;
      const ids = Object.keys(agents);
      const found = agents[target!.agentId];
      if (found) {
        state.selectAgent(target!.agentId);
        clearFragment();
        return true;
      }
      if (ids.length === 0) return false;
      // Loaded, target absent.
      useToastStore.getState().push({
        message: 'Agent not found',
        durationMs: 5_000,
      });
      clearFragment();
      return true;
    }

    if (tryResolve()) return;

    // Subscribe — re-attempt every time agents map changes. Bounded by a
    // hard timeout so a stuck loadAgents() doesn't leave the hook open.
    const unsubscribe = useAppStore.subscribe((s, prev) => {
      if (s.agents === prev.agents) return;
      if (tryResolve()) {
        unsubscribe();
        clearTimeout(timer);
      }
    });
    const timer = setTimeout(() => {
      unsubscribe();
      // Last attempt — if still no agents, surface the failure path so
      // the user isn't left wondering.
      if (Object.keys(useAppStore.getState().agents).length === 0) {
        useToastStore.getState().push({
          message: 'Agent not found',
          durationMs: 5_000,
        });
      }
      clearFragment();
    }, timeoutMs);
  }, [win, timeoutMs]);
}
